/**
 * The pieces every Cashflow Control tab shares.
 *
 * The column frame lives here rather than in each tab because the three tabs
 * are meant to be read across: a reader who has learned that the yellow columns
 * are budget on the Opex tab should not have to learn it again on GP. Three
 * copies of the frame would drift, and the drift would be invisible — every
 * number on screen would still look right.
 */

import React from 'react';
import dayjs from 'dayjs';

export const INK = '#262B40';
export const NEUTRAL = '#7c8ba0';
export const OVER = '#a32f2a';
export const UNDER = '#146c43';
export const BUDGET_INK = '#8a6d1f';
export const BUDGET_BG = '#fffdf6';
export const FAINT = '#d0d7e0';

export const fmtMoney = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString() : Math.abs(n).toFixed(0);
  return `${n < 0 ? '-$' : '$'}${String(s).replace('-', '')}`;
};

export const fmtPct = (v, dp = 1) => (v == null ? '—' : `${Number(v).toFixed(dp)}%`);

/** A figure, or a dash that is visibly not a zero. */
export const moneyCell = (v, colour) => (v
  ? <span style={{ fontVariantNumeric: 'tabular-nums', color: colour || INK }}>{fmtMoney(v)}</span>
  : <span style={{ color: FAINT }}>—</span>);

/**
 * The eight columns: three months of actual, this month so far, then budget for
 * this month and the three ahead.
 *
 * `idx` indexes into a seven-month window, so this month appears twice — once
 * as what has happened and once as what was planned. That is the comparison the
 * page exists to make, and giving each its own column is what stops one being
 * mistaken for the other.
 */
export const FRAME = [
  { key: 'm3', idx: 0, kind: 'actual' },
  { key: 'm2', idx: 1, kind: 'actual' },
  { key: 'm1', idx: 2, kind: 'actual' },
  { key: 'mtd', idx: 3, kind: 'mtd' },
  { key: 'b0', idx: 3, kind: 'budget' },
  { key: 'b1', idx: 4, kind: 'budget' },
  { key: 'b2', idx: 5, kind: 'budget' },
  { key: 'b3', idx: 6, kind: 'budget' },
];

const KIND_LABEL = { mtd: 'MTD actual', budget: 'budget', actual: 'actual' };

/**
 * Build the frame's columns against a months array.
 *
 * `pick(row, month, kind)` renders one cell; the caller decides what a budget
 * cell and an actual cell mean for its own rows.
 */
export function frameColumns(months, pick, opts = {}) {
  return FRAME
    .filter((f) => !opts.actualsOnly || f.kind !== 'budget')
    .map((f) => {
      const m = months[f.idx] || null;
      const isBudget = f.kind === 'budget';
      return {
        key: f.key,
        width: opts.width || 118,
        align: 'right',
        title: (
          <div style={{ lineHeight: 1.25 }}>
            <div>{m ? dayjs(m).format('MMM YY') : '—'}</div>
            <div style={{ fontSize: 10, fontWeight: 400, color: isBudget ? BUDGET_INK : NEUTRAL }}>
              {opts.budgetLabel && isBudget ? opts.budgetLabel : KIND_LABEL[f.kind]}
            </div>
          </div>
        ),
        onHeaderCell: () => ({ style: isBudget ? { background: BUDGET_BG } : undefined }),
        onCell: () => ({ style: isBudget ? { background: BUDGET_BG } : undefined }),
        render: (_, row) => pick(row, m, f.kind),
      };
    });
}
